import { escapeRegExp } from 'lodash'

import { CHANNEL_TYPE } from '@/utils/constants'

export const isSmsOrChatMessage = (messageType) =>
  messageType === CHANNEL_TYPE.SMS || messageType === CHANNEL_TYPE.CHAT

export const safelyDecodeURI = (encodedString) => {
  try {
    return decodeURIComponent(encodedString)
  } catch {
    return encodedString
  }
}

export const cleanMessageHtml = (rawHtml) => {
  const parser = new DOMParser()
  // TODO: Check - Next line is maybe not necesary. It's added since we had URL encoded like data
  let html = safelyDecodeURI(rawHtml)
  // Pre-chat surveys and SMS messages arrive as plain text with raw \n characters;
  // DOMParser does not preserve these as line breaks, so inject <br> before parsing.
  if (!/<[a-z][a-z0-9]*(\s[^>]*)?\/?>|<\/[a-z][a-z0-9]*>/i.test(html)) {
    html = html.replace(/\r\n|\r|\n/g, '<br>')
  }
  const doc = parser.parseFromString(html, 'text/html')
  const removeProblematicStyles = (document) => {
    const patternsToStrip = [
      /@media\b/i,
      /:root\b/i,
      /@font-face\b/i,
      /@keyframes\b/i,
      /(\n|^)\s*(html|body)\s*[{,]/i,
    ]
    const styleTags = document.querySelectorAll('style')
    styleTags.forEach((styleTag) => {
      const cssText = styleTag.textContent || ''
      const shouldRemoveStyleTag = patternsToStrip.some((pattern) => pattern.test(cssText))
      if (shouldRemoveStyleTag) {
        styleTag.remove()
      }
    })
  }
  const ignorableTags = ['BR', 'DIV', 'SPAN', 'B', 'I', 'U']

  const isBlankNode = (node) => {
    if (!(node instanceof HTMLElement)) return false
    const tag = node.tagName
    if (!ignorableTags.includes(tag)) return false

    if (!node.hasChildNodes()) {
      return !node.textContent.trim()
    }

    const children = Array.from(node.childNodes)
    return children.every((child) => {
      if (child.nodeType === Node.TEXT_NODE) {
        return !child.textContent.trim()
      }
      if (child.nodeType === Node.ELEMENT_NODE) {
        return ignorableTags.includes(child.tagName) && isBlankNode(child)
      }
      return true
    })
  }

  const trimTrailingBlanks = (parent) => {
    const children = Array.from(parent.childNodes)
    for (let i = children.length - 1; i >= 0; i--) {
      const child = children[i]
      if (child.nodeType === Node.ELEMENT_NODE) {
        if (isBlankNode(child)) {
          parent.removeChild(child)
        } else {
          // Go deeper only if needed
          trimTrailingBlanks(child)
          break
        }
      } else if (child.nodeType === Node.TEXT_NODE) {
        if (!child.textContent.trim()) {
          parent.removeChild(child)
        } else {
          break
        }
      } else {
        parent.removeChild(child)
      }
    }
  }

  const convertRedactedSpans = (document) => {
    const html = document.body.innerHTML
    const converted = html.replace(
      /&lt;span\s+class="(redacted[^"]*?)"\s+data-entity="(\w+)"&gt;([^&]*?)&lt;\/span&gt;/g,
      '<span class="$1" data-entity="$2">$3</span>'
    )
    if (converted !== html) {
      document.body.innerHTML = converted
    }
  }

  // Remove styles that attempt to override the app theme
  removeProblematicStyles(doc)
  convertRedactedSpans(doc)
  trimTrailingBlanks(doc.body)
  return doc
}

export const collapseMessageHtml = (doc) => {
  const collapsedDoc = doc.cloneNode(true)

  const htmlToInlineText = (node) => {
    node.childNodes.forEach((child) => {
      if (child.nodeType === 1) {
        if (['P', 'DIV', 'BR'].includes(child.tagName)) {
          const span = document.createElement('span')
          span.innerHTML = child.innerHTML.trim()
          const space = document.createTextNode(' ')
          child.replaceWith(span, space)

          htmlToInlineText(span)
        } else {
          htmlToInlineText(child)
        }
        if (!child.textContent.trim()) child.remove()
      }
    })
  }

  htmlToInlineText(collapsedDoc.body)
  return collapsedDoc.body.innerHTML.trim()
}

export const getCollapsedMessageHtml = (rawHtml, hasQuoted = false) => {
  const doc = cleanMessageHtml(rawHtml)
  const collapsedText = collapseMessageHtml(doc)
  const shouldAppendDots = hasQuoted && collapsedText.length > 0
  return shouldAppendDots ? `${collapsedText}...` : collapsedText
}

export const highlightSearch = (doc, searchTerm, matchIndices, currentSearchIndex) => {
  if (!searchTerm?.trim()) return { html: doc.body.innerHTML, matchCount: 0 }

  const regex = new RegExp(`(${escapeRegExp(searchTerm)})`, 'gi')
  let matchIndex = 0

  const highlightMatchesInNode = (node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      const matches = node.textContent.match(regex)
      if (matches) {
        const span = document.createElement('span')
        span.innerHTML = node.textContent.replace(regex, (match) => {
          const isCurrent = matchIndices[matchIndex] === currentSearchIndex
          const cls = isCurrent ? 'search-highlight current-match' : 'search-highlight'
          matchIndex++
          return `<mark class="${cls}">${match}</mark>`
        })

        node.replaceWith(...span.childNodes)
      }
    } else if (node.nodeType === Node.ELEMENT_NODE) {
      for (const child of Array.from(node.childNodes)) {
        highlightMatchesInNode(child)
      }
    }
  }

  highlightMatchesInNode(doc.body)
  return { html: doc.body.innerHTML, matchCount: matchIndex }
}

/**
 * Ensures that an ISO 8601 datetime string has a UTC timezone indicator ('Z').
 * If the input string does not end with 'Z', it appends 'Z' to indicate UTC.
 *
 * @param {string} dateTimeString - An ISO 8601 datetime string, possibly without a timezone indicator.
 * @returns {string} The ISO 8601 datetime string with a UTC timezone indicator.
 */
export const getDateTimeISO = (dateTimeString) =>
  dateTimeString.endsWith('Z') ? dateTimeString : `${dateTimeString}Z`

/**
 * Generate Unix dateTime number as string so we can use it as an anchor to highlight or jump to specific message
 *
 * @param {*} dateTimeISO - An ISO 8601 datetime string
 * @returns {string} Anchor ID - unix type dateTime
 */
export const getAnchorId = (dateTimeISO) => {
  const anchorTime = new Date(dateTimeISO).getTime()
  return Math.floor(anchorTime / 1000).toString()
}

export const splitQuotedContent = (doc) => {
  if (!doc || !doc.body) return { mainContent: '', quotedSections: [] }

  const body = doc.body.cloneNode(true)
  const quotedSections = []

  // First, handle Gmail quotes as complete units - use more specific selector to avoid duplicates
  const gmailQuotes = body.querySelectorAll('.gmail_quote')
  gmailQuotes.forEach((element) => {
    if (element.parentNode) {
      quotedSections.push({
        html: element.outerHTML,
        type: 'replied',
      })
      element.remove()
    }
  })

  // Handle Outlook and other email client patterns only if no Gmail quotes were found
  if (quotedSections.length === 0) {
    const otherQuotedSelectors = [
      // Outlook patterns
      '[id^="divRplyFwdMsg"]', // Outlook reply/forward divider
      '.OutlookMessageHeader',
      '[style*="border-top: #B5C4DF 1pt solid"]', // Outlook separator line
      // Apple Mail
      '.AppleMailSignature + *',
      // Thunderbird
      '.moz-cite-prefix + blockquote',
      // Generic patterns
      'blockquote[type="cite"]',
      'blockquote[style*="border-left"]',
      // Elements with specific quote styling
      '[style*="border-left: 1px solid"]',
      '[style*="margin-left: 40px"]', // Common indentation for quotes
    ]

    otherQuotedSelectors.forEach((selector) => {
      try {
        const elements = body.querySelectorAll(selector)
        elements.forEach((element) => {
          if (element.parentNode) {
            quotedSections.push({
              html: element.outerHTML,
              type: 'replied',
            })
            element.remove()
          }
        })
      } catch (e) {
        // Ignore selector errors
      }
    })
  }

  // Text-based patterns to detect quoted content (only if no structural quotes found)
  if (quotedSections.length === 0) {
    const quotedTextPatterns = [
      // Gmail/common patterns
      /^On .* wrote:$/m,
      /^On .* at .* wrote:$/m,
      // Outlook patterns
      /^From: .*$/m,
      /^Sent: .*$/m,
      /^To: .*$/m,
      /^Subject: .*$/m,
      /^Date: .*$/m,
      /^-----Original Message-----/m,
      /^________________________________/m,
      // Apple Mail
      /^Begin forwarded message:/m,
      // Generic patterns
      /^> /m, // Line starts with >
      /^[ \t]*>[^\r\n]*$/m, // Line starts with > (no catastrophic backtracking))
      // Outlook web patterns
      /^From:.*Sent:.*To:.*Subject:/m,
    ]

    const walker = body.ownerDocument.createTreeWalker(body, NodeFilter.SHOW_TEXT, null, false)

    const textNodes = []
    let node = walker.nextNode()
    while (node) {
      textNodes.push(node)
      node = walker.nextNode()
    }

    // Group processing to avoid processing the same quoted block multiple times
    const processedElements = new Set()

    textNodes.forEach((textNode) => {
      const text = textNode.textContent.trim()
      const isQuotedLine = quotedTextPatterns.some((pattern) => pattern.test(text))

      if (isQuotedLine) {
        // Find the containing element that should be treated as quoted
        let quotedElement = textNode.parentElement

        // Skip if we've already processed this element
        if (processedElements.has(quotedElement)) return

        // Look for a reasonable container (not body or html)
        while (
          quotedElement &&
          (quotedElement.tagName === 'BODY' ||
            quotedElement.tagName === 'HTML' ||
            quotedElement.children.length === 1)
        ) {
          quotedElement = quotedElement.parentElement
        }

        if (quotedElement && quotedElement.parentNode && !processedElements.has(quotedElement)) {
          // Mark as processed
          processedElements.add(quotedElement)

          // Check if this element or its siblings contain the quoted content
          let sibling = quotedElement.nextElementSibling
          let quotedHtml = quotedElement.outerHTML

          // Collect subsequent siblings that are likely part of the quote
          while (sibling && !processedElements.has(sibling)) {
            const siblingText = sibling.textContent.trim()
            const isPartOfQuote =
              quotedTextPatterns.some((pattern) => pattern.test(siblingText)) ||
              siblingText.startsWith('>') ||
              sibling.tagName === 'BLOCKQUOTE'

            if (isPartOfQuote) {
              processedElements.add(sibling)
              quotedHtml += sibling.outerHTML
              const nextSibling = sibling.nextElementSibling
              sibling.parentNode.removeChild(sibling)
              sibling = nextSibling
            } else {
              break
            }
          }

          quotedSections.push({
            html: quotedHtml,
            type:
              text.includes('wrote:') ||
              text.includes('From:') ||
              text.startsWith('>') ||
              text.includes('&gt;')
                ? 'replied'
                : 'forwarded',
          })
          quotedElement.remove()
        }
      }
    })
  }

  if (quotedSections.length > 1) {
    quotedSections.splice(1)
  }

  return {
    mainContent: body.innerHTML.trim(),
    quotedSections,
  }
}

const getTextLength = (html) => {
  if (!html) return 0
  const parser = new DOMParser()
  const doc = parser.parseFromString(html, 'text/html')
  return doc.body.textContent.trim().length
}

export const isMessageExpandable = (messageHtml, threshold = 60) => {
  if (!messageHtml) return false
  const cleanedDoc = cleanMessageHtml(messageHtml)
  const { mainContent, quotedSections } = splitQuotedContent(cleanedDoc)
  const contentLength = getTextLength(mainContent)

  return quotedSections.length > 0 || contentLength > threshold
}

export const shouldExpandMessage = ({
  allExpanded,
  searchQuery,
  currentMatchMessageId,
  messageId,
  selectedKeyword,
  hasKeyword,
  isLastMessage,
  defaultExpandedMessageId,
}) => {
  if (allExpanded === true) return true
  if (searchQuery && currentMatchMessageId) {
    return String(messageId) === String(currentMatchMessageId)
  }
  if (selectedKeyword) {
    return hasKeyword || isLastMessage
  }
  if (allExpanded === false) return false
  return messageId === defaultExpandedMessageId
}

// Per-integration thread_id visibility — providers whose thread_id is derived from PII
// (e.g. Sinch builds it from a hashed phone number) should not have it surfaced in the UI.
// Order matters: longer suffixes must come before shorter ones to avoid partial matches.
const PROVIDER_THREAD_ID_CONFIG = [
  { suffix: '_nice_cx_one_dfo', visible: true },
  { suffix: '_text_livechat', visible: true },
  { suffix: '_salesforce', visible: true },
  { suffix: '_default', visible: true },
  { suffix: '_hubspot', visible: true },
  { suffix: '_twilio', visible: true },
  { suffix: '_sinch', visible: false },
  { suffix: '_8x8', visible: true },
]

const matchProviderConfig = (id) => {
  if (typeof id !== 'string') return null
  return PROVIDER_THREAD_ID_CONFIG.find((entry) => id.endsWith(entry.suffix)) || null
}

/**
 * Whether the given thread_id is safe to display in the UI. Returns false only for providers
 * we know construct their thread identifier from PII (Sinch). For unknown providers or null
 * we default to true — the parser will simply pass the value through.
 */
export const isThreadIdVisible = (threadId) => {
  const config = matchProviderConfig(threadId)
  if (!config) return true
  return config.visible
}

/**
 * Strip Balto-internal wrappers from an externally-built id (eml_message_id or thread_id).
 * Pattern is `{org_id?}_{customer_id}_{provider_suffix}` — strip the known provider suffix
 * and the leading numeric org_id (when a suffix was matched).
 *
 * Returns the id unchanged when:
 * - the id is null/empty/non-string
 * - no known provider suffix matches (e.g. Sinch eml_message_id is the raw provider value)
 * - the matched provider is marked `visible: false` (defense in depth — if a non-visible
 *   provider ever started wrapping PII inside our standard envelope, the parser must not
 *   unwrap it and surface the inner value)
 */
export const parseExternalId = (id) => {
  if (!id || typeof id !== 'string') return id
  const config = matchProviderConfig(id)
  if (!config || !config.visible) return id
  const remaining = id.slice(0, -config.suffix.length).replace(/^\d+_/, '')
  return remaining || id
}

export const calculateToggleState = ({
  allExpanded,
  searchQuery,
  totalMatches,
  currentSearchIndex,
  matchIndexMap,
  selectedKeyword,
}) => {
  if (allExpanded === true) {
    const result = { allExpanded: false }

    if ((searchQuery || selectedKeyword) && totalMatches > 0) {
      const validIndex =
        currentSearchIndex >= 0 && currentSearchIndex < totalMatches ? currentSearchIndex : 0

      const messageWithCurrentMatch = Object.keys(matchIndexMap).find((messageId) =>
        matchIndexMap[messageId]?.includes(validIndex)
      )

      result.currentSearchIndex = validIndex
      result.currentMatchMessageId = messageWithCurrentMatch || null
    } else {
      result.currentMatchMessageId = null
    }

    return result
  }
  return {
    allExpanded: true,
    currentMatchMessageId: null,
  }
}
